/**
 * Flight service — flight auto-populate lookups for the event modal.
 *
 * Calls the backend `GET /flights/{tailNumber}` endpoint, which returns the
 * previous flight the tail arrived on and the next flight it is scheduled to
 * operate, sourced from APES tail-mapping data. Used to pre-fill the previous /
 * chargeable / next-scheduled flight fields when a tail is selected.
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';

const logger = createLogger('FlightService');

/** A single flight record as returned by the flight auto-populate endpoint. */
export interface FlightInfo {
  flightNumber: string;
  carrier: string;
  origin: string;
  destination: string;
  scheduledDepartureTime: number;
  scheduledArrivalTime: number;
  actualDepartureTime: number | null;
  actualArrivalTime: number | null;
  isCancelled: boolean;
}

/** Response shape of `GET /flights/{tailNumber}`. */
export interface FlightAutoPopulate {
  tailNumber: string;
  asOf: number | null;
  /** Authoritative APES carrier for the tail (from the aircraft-info cache). */
  carrier: string;
  previousFlight: FlightInfo | null;
  /** The flight immediately before previousFlight (used by RTB/ATO/ATB mapping). */
  previousPriorFlight: FlightInfo | null;
  nextFlight: FlightInfo | null;
}

/**
 * Fetches previous/next flight info for a tail. `at` (epoch seconds) anchors the
 * lookup; when omitted the backend defaults to the current time. Returns null on
 * any auth/network/API error so callers can no-op without disrupting the form.
 */
export async function getFlightAutoPopulate(
  tailNumber: string,
  at?: number,
): Promise<FlightAutoPopulate | null> {
  const tail = tailNumber.trim();
  if (!tail) return null;
  const query = at !== undefined && Number.isFinite(at) ? `?at=${Math.floor(at)}` : '';

  try {
    const response = await authedFetch(`/flights/${encodeURIComponent(tail)}${query}`);
    if (!response || !response.ok) {
      if (response) {
        logger.error('Failed to fetch flight auto-populate', {
          status: response.status,
          tailNumber: tail,
        });
      }
      return null;
    }
    return (await response.json()) as FlightAutoPopulate;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('getFlightAutoPopulate failed', { error: message, tailNumber: tail });
    return null;
  }
}
